import React, { useState, useEffect } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { secureGet, securePost } from "../../config/api";
import { Helmet } from "react-helmet-async";
import { SaveCancelTop } from "../../components/PrimaryButton";
import Input from "../../components/Input";
import SnackbarNotification from "../../components/SnackbarNotification";
import Breadcrumbs from "../../components/Breadcrumbs";
import LoadingLogo from "../../components/LoadingLogo";
import { formatAmount } from "../../utils/format";

import plusIcon from "../../icons/plus.svg";
import DeleteIcon from "../../icons/trashR.svg";

const toDateInputValue = (v) => {
  if (!v) return "";
  if (typeof v === "string" && v.length >= 10) return v.slice(0, 10);
  try {
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  } catch (_) {}
  return "";
};

const InvoiceForm = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();

  const [invoice, setInvoice] = useState(null);
  const [subscriptionTvaRate, setSubscriptionTvaRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const tvaRate = subscriptionTvaRate ?? (parseFloat(process.env.REACT_APP_TVA) || 0);

  const [formValues, setFormValues] = useState({
    grayClientId: "",
    invoiceNumber: "",
    createdAt: "",
    periodEnd: "",
    subscriptionId: "",
  });

  const [listEntities, setListEntities] = useState([]);
  const [currentLine, setCurrentLine] = useState({ itemType: "", unitPrice: "" });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    const abortController = new AbortController();
    const fetchInvoice = async () => {
      if (!id) return;
      try {
        const response = await secureGet(`/billing-invoices/${id}`, showSnackbar, {
          signal: abortController.signal,
        });
        const data = response.data;
        console.log(data);
        setInvoice(data);
        setFormValues({
          grayClientId: String(data.grayClient?.companyName  ?? ""),
          invoiceNumber: data.invoiceNumber ?? "",
          createdAt: toDateInputValue(data.createdAt),
          periodEnd: toDateInputValue(data.periodEnd),
          subscriptionId: String(data.subscriptionId ?? ""),
        });
        const details = data.details ?? data.lineItems ?? data.items ?? [];
        setListEntities(
          details.map((item) => {
            const price = Number(item.unitPrice ?? item.price ?? item.unitPriceHT ?? 0) || 0;
            return {
              itemType: item.itemType ?? item.type ?? item.description ?? "",
              unitPrice: price,
              description: item.description ?? "",
              isNew: false,
            };
          })
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Failed to fetch invoice:", error);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
    return () => abortController.abort();
  }, [id]);

  useEffect(() => {
    const subscriptionId = invoice?.subscriptionId;
    if (!subscriptionId) return;
    const abortController = new AbortController();
    const fetchSubscription = async () => {
      try {
        const response = await secureGet(`/subscriptions/${subscriptionId}`, showSnackbar, {
          signal: abortController.signal,
        });
        const data = response?.data ?? response;
        let rate = Number(data?.subscriptionTVA ?? data?.tva);
        if (Number.isFinite(rate)) {
          if (rate > 1) rate = rate / 100;
          setSubscriptionTvaRate(rate);
        }
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.error("Failed to fetch subscription for TVA", err);
        }
      }
    };
    fetchSubscription();
    return () => abortController.abort();
  }, [invoice?.subscriptionId]);

  const totalHT = (invoice?.priceHT ?? 0) + listEntities
    .filter((row) => row.isNew)
    .reduce((acc, row) => acc + (Number(row.unitPrice) ?? Number(row.price) ?? 0), 0);
  const totalTTC = (invoice?.priceTTC ?? 0) + listEntities
    .filter((row) => row.isNew)
    .reduce((acc, row) => acc + ((Number(row.unitPrice) ?? Number(row.price) ?? 0) * (1 + tvaRate)), 0);

  const handleInputChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const addListEntity = () => {
    const { itemType, unitPrice } = currentLine;
    if (unitPrice === "" || Number(unitPrice) < 0) {
      showSnackbar(t("errors.requiredFields") || "Veuillez remplir le prix unitaire", "error");
      return;
    }
    setListEntities((prev) => [
      ...prev,
      {
        itemType: itemType || "",
        unitPrice: Number(unitPrice) || 0,
        isNew: true,
      },
    ]);
    setCurrentLine({ itemType: "", unitPrice: "" });
  };

  const removeListEntity = (index) => {
    setListEntities((prev) => prev.filter((_, i) => i !== index));
  };

  const handleLineChange = (index, field, value) => {
    setListEntities((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: field === "unitPrice" ? (value === "" ? "" : Number(value)) : value };
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!id) return;
    try {
      const newLinesOnly = listEntities.filter((row) => row.isNew);
      for (const row of newLinesOnly) {
        await securePost(
          `/billing-invoices/${id}/manual-line`,
          {
            itemType: row.itemType || "SUBSCRIPTION",
            price: Number(row.unitPrice) || 0,
          },
          showSnackbar
        );
      }
      showSnackbar(t("confirmation.invoicesEdit") || "Facture modifiée avec succès", "success");
      history.push({
        pathname: "/invoices",
        state: {
          filters: location.state?.filters || {},
          page: location.state?.page || 0,
          size: location.state?.size || 30,
          showSnackbar: true,
          snackbarMessage: t("confirmation.invoicesEdit"),
          snackbarSeverity: "success",
        },
      });
    } catch (error) {
      console.error("Failed to add manual line(s):", error);
    }
  };

  const handleCancel = () => {
    history.push({
      pathname: "/invoices",
      state: {
        filters: location.state?.filters || {},
        page: location.state?.page || 0,
        size: location.state?.size || 30,
      },
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[300px]">
        <LoadingLogo />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-6">
        <p className="text-gray-500">{t("errors.generic") || "Impossible de charger la facture."}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Helmet>
        <title>GrayFox - {t("titles.invoice_details")} - {formValues.invoiceNumber}</title>
      </Helmet>
      <Breadcrumbs />
      <div className="flex justify-between items-center">
        <h2 className="text-[32px] font-bold text-black">{t("titles.invoice_modify") || "Modifier la facture"}</h2>
        <SaveCancelTop onCancel={handleCancel} onSave={handleSubmit} />
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Display fields */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Input
              label="GrayClient"
              name="grayClientId"
              value={formValues.grayClientId}
              onChange={(v) => handleInputChange("grayClientId", v)}
              disabled={true}
            />
            <Input
              label={t("fields.invoice.invoiceNumber") || "N° Facture"}
              name="invoiceNumber"
              value={formValues.invoiceNumber}
              onChange={(v) => handleInputChange("invoiceNumber", v)}
              disabled={true}
            />
            <Input
              label={t("columns.createdAt") || "Émis le"}
              name="issuedAt"
              type="date"
              value={formValues.createdAt}
              onChange={(v) => handleInputChange("createdAt", v)}
              disabled={true}
            />
            <Input
              label={t("columns.periodEnd") || "Fin de période"}
              name="periodEnd"
              type="date"
              value={formValues.periodEnd}
              onChange={(v) => handleInputChange("periodEnd", v)}
              disabled={true}
            />
          </div>
        </div>

        <div className="space-y-[10px]">
          <h3 className="text-[16px] font-semibold text-[#ACAFC6]">{t("fields.invoice.details")}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Input
              label={t("fields.invoice.itemType") || "Item"}
              name="itemType"
              type="dropdown"
              value={currentLine.itemType}
              options={[
                { value: "INITIAL_SETUP", label: t("fields.invoice.itemType_INITIAL_SETUP") || "Initial setup" },
                { value: "MAINTENANCE", label: t("fields.invoice.itemType_MAINTENANCE") || "Maintenance" },
              ]}
              onChange={(v) => setCurrentLine((prev) => ({ ...prev, itemType: v }))}
            />
            <Input
              label="Prix HT"
              name="price"
              type="text"
              value={currentLine.unitPrice}
              onChange={(v) => setCurrentLine((prev) => ({ ...prev, unitPrice: v }))}
            />
            <button
              type="button"
              onClick={addListEntity}
              className="bg-[#043F88] text-white w-[48px] h-[48px] rounded-md hover:bg-[#3F3456] flex items-center justify-center"
            >
              <img src={plusIcon} alt="Add" className="w-4 h-4" />
            </button>
          </div>
        </div>

        {listEntities.length > 0 && (
          <div className="space-y-[10px]">
            {listEntities.map((row, index) => {
              const price = Number(row.unitPrice) ?? Number(row.price) ?? 0;
              const priceTTC = price * (1 + tvaRate);
              return (
                <div
                  key={index}
                  className="grid grid-cols-[2fr_1fr_1fr_auto] items-center bg-white px-4 py-2 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)] gap-4"
                >
                  <div>
                    <p className="text-[#043F88] text-[16px] font-semibold">
                      {row.itemType ? (t(`fields.invoice.itemType_${row.itemType}`) || row.itemType) : ''}
                    </p>
                    <p className="text-[#38485C] text-[14px] mt-0.5">{row.description ?? ''}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[#707070] text-[12px] font-semibold">{t('fields.invoice.totalHT')}</p>
                    <p className="text-[#043F88] text-[20px] font-semibold">{formatAmount(price)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[#707070] text-[12px] font-semibold">{t('fields.invoice.totalTTC')}</p>
                    <p className="text-[#043F88] text-[20px] font-semibold">{formatAmount(priceTTC)}</p>
                  </div>
                  <div className="flex justify-center">
                    {row.isNew && (
                      <button
                        type="button"
                        onClick={() => removeListEntity(index)}
                        className="w-8 h-8 flex items-center justify-center bg-[#F9FAFC] rounded-[12px] border border-[#EF466F4D]"
                      >
                        <img src={DeleteIcon} alt="Delete" className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Totals: invoice.priceHT/priceTTC + sum of listEntities */}
        <div className="flex justify-between items-center pt-4 ">
          <div className="flex gap-8">
            <div className="flex items-center gap-2">
              <span className="text-[16px] font-medium text-[#231E29]">{t("fields.invoice.totalHT") || "Total HT"}:</span>
              <span className="text-[16px] font-bold text-[#043F88]">{formatAmount(totalHT)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[16px] font-medium text-[#231E29]">{t("fields.invoice.totalTTC") || "Total TTC"}:</span>
              <span className="text-[16px] font-bold text-[#043F88]">{formatAmount(totalTTC)}</span>
            </div>
          </div>
        </div>
      </form>

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default InvoiceForm;
